<template>
    <v-container class="pa-0 profile-content">

        <profile-avatar class="profile-avatar-block" :img="avatarImg" :name="companyName" :size="100" />

        <div class="profile-stats">
            <div class="stat-cell">
                <div class="stat-icon-row">
                    <v-icon size="16" color="txtMuted">mdi-account-multiple-outline</v-icon>
                    <span class="stat-value">{{ coachesCount }}</span>
                </div>
                <div class="stat-label">{{ $tc('coach.coach', 2) }}</div>
            </div>
            <div class="stat-cell">
                <div class="stat-icon-row">
                    <v-icon size="16" color="txtMuted">mdi-account-outline</v-icon>
                    <span class="stat-value">{{ studentsCount }}</span>
                </div>
                <div class="stat-label">{{ $t('common.actives') }}</div>
            </div>
        </div>

        <div class="profile-name-wrap">
            <div class="profile-name">{{ companyName }}</div>
            <button-tooltip @click="editBasicInfo" icon top :tooltip="$t('common.edit')" buttonClass="edit-name-btn">
                <v-icon size="15">mdi-square-edit-outline</v-icon>
            </button-tooltip>
        </div>
        <div class="profile-desc">{{ description }}</div>

        <div class="profile-details">
            <div v-for="(detail, i) in details" :key="i" class="detail-row">
                <span class="detail-label">{{ detail.name }}</span>
                <span class="detail-value">{{ detail.value }}</span>
            </div>
        </div>

        <edit-company-info-dialog ref="basicInfo" />
    </v-container>
</template>

<script>

import { mapActions } from 'vuex'
import ProfileAvatar from '@/components/common/ProfileAvatar'
import ButtonTooltip from '@/components/common/ButtonTooltip'
import EditCompanyInfoDialog from '@/components/company/EditCompanyInfoDialog'

export default {
    name: "CompanyProfile",
    components: {
        ProfileAvatar,
        ButtonTooltip,
        EditCompanyInfoDialog,
    },
    data(vm) {
        return {
            coaches: [],
            typesOptions: [
                { name: vm.$t('company.types.club'), value: 'club' },
                { name: vm.$t('company.types.gym'), value: 'gym' },
                { name: vm.$t('company.types.institution'), value: 'institution' }
            ],
        }
    },
    computed: {
        avatarImg(vm = this) {
            return vm.account?.avatar || null
        },
        companyName(vm = this) {
            return vm.account?.name || vm.$t('common.no_name')
        },
        description(vm = this) {
            return vm.account?.description || vm.$t('common.no_description')
        },
        coachesCount(vm = this) {
            return vm.coaches.length
        },
        studentsCount(vm = this) {
            return vm.coaches.reduce((total, coach) => total + Number(coach.activeStudents || 0), 0)
        },
        type(vm = this) {
            if (!vm.account?.type) return vm.$t('common.not_available_short')
            return vm.typesOptions.find(type => type.value === vm.account.type)?.name || vm.$t('common.not_available_short')
        },
        phone(vm = this) {
            if (!vm.account?.phone) return vm.$t('common.not_available_short')
            return `${vm.account.phoneCode || vm.account.country?.phoneCode || ''} ${vm.account.phone}`.trim()
        },
        details(vm = this) {
            return [
                { name: vm.$t('common.email'), value: vm.account?.email || vm.$t('common.not_available_short') },
                { name: vm.$t('common.phone'), value: vm.phone },
                { name: vm.$t('company.address'), value: vm.account?.address || vm.$t('common.not_available_short') },
                { name: vm.$t('company.type'), value: vm.type },
                { name: vm.$t('entrance.experience'), value: vm.account?.experience || vm.$t('common.not_available_short') },
            ]
        },
    },
    async mounted() {
        const vm = this
        if (!vm.currentUser?.uid) return
        vm.coaches = await vm.fetchCompanyCoaches(vm.currentUser.uid) || []
    },
    methods: {
        ...mapActions(['fetchCompanyCoaches']),
        editBasicInfo() {
            const vm = this
            vm.$refs.basicInfo.show()
        },
    },
}
</script>

<style scoped>

.profile-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 32px;
}

.profile-avatar-block {
    margin-bottom: 24px;
}

.profile-stats {
    display: flex;
    width: 100%;
    max-width: 480px;
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
    overflow: hidden;
    background: var(--v-surface-base);
    margin-bottom: 24px;
}

.stat-cell {
    flex: 1;
    padding: 14px 20px;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.stat-cell + .stat-cell {
    border-left: 1px solid var(--v-outline-base);
}

.stat-icon-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.stat-value {
    font-size: 20px;
    font-weight: 800;
    color: var(--v-txtPrimary-base);
}

.stat-label {
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
    font-weight: 500;
}

.profile-name-wrap {
    width: 100%;
    max-width: 480px;
    position: relative;
    margin-bottom: 8px;
}

.profile-name {
    font-size: 22px;
    font-weight: 800;
    color: var(--v-txtPrimary-base);
    text-align: center;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--v-primary-base);
}

.theme--light .profile-name {
    border-color: var(--v-accent-base);
}

.profile-content >>> .edit-name-btn {
    position: absolute;
    right: 0;
    top: 0;
    width: 28px !important;
    height: 28px !important;
    color: var(--v-txtMuted-base) !important;
}

.profile-desc {
    font-size: var(--fs-label);
    color: var(--v-txtMuted-base);
    text-align: center;
    margin-top: 10px;
}

.profile-details {
    width: 100%;
    max-width: 480px;
    margin-top: 24px;
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
    background: var(--v-surface-base);
    overflow: hidden;
}

.detail-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--v-outline-base);
}

.detail-row:last-child {
    border-bottom: none;
}

.detail-label {
    font-size: var(--fs-caption);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--v-txtMuted-base);
    flex-shrink: 0;
}

.detail-value {
    font-size: var(--fs-body);
    color: var(--v-txtPrimary-base);
    text-align: right;
    overflow: hidden;
    text-overflow: ellipsis;
}

</style>
